import { useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useRef, useState } from 'react';

import UDConfirmModal from './confirm-modal';
import { ModalFrame, UDModalProps } from './modal-frame';

export type { UDModalProps, UDModalSize } from './modal-frame';

/**
 * Окно кита. Рама — `ModalFrame` (нативный `<dialog>`, положение, Escape-стек); здесь — защита
 * формы с правками от случайного закрытия (IMGM-1623).
 *
 * Форма внутри окна сообщает о правках через `useModalLayer().setDirty` (готовый мост для Formik —
 * `UDModalDirtyGuard` из `ud-form`). Без правок клик вне окна, Escape и крестик закрывают окно
 * сразу. С правками — сначала `Modal.Confirm` «Бросить изменения?»: «Остаться» оставляет окно и
 * ввод, «Бросить» зовёт `onClose`. Кнопки самой формы («Отмена», «Сохранить») окно не трогает —
 * они зовут свои обработчики напрямую.
 *
 * Флаг правок — `ref`, а не состояние: форма не перерисовывает окно на каждый ввод, а окно читает
 * флаг только в момент закрытия.
 */
const UDModal = ({ onClose, ...props }: UDModalProps) => {
  const { t } = useLingui();
  const dirtyRef = useRef(false);
  const [isAskingDiscard, setIsAskingDiscard] = useState(false);

  const setDirty = useCallback((dirty: boolean) => {
    dirtyRef.current = dirty;
  }, []);
  const holdsOpen = useCallback(() => dirtyRef.current, []);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const requestClose = useCallback(() => {
    if (dirtyRef.current) setIsAskingDiscard(true);
    else onCloseRef.current?.();
  }, []);

  // «Бросить» закрывает окна сверху вниз: сначала вопрос, а форму — эффектом после его закрытия.
  // Иначе первой закрывается форма (фокус в вопросе, браузер его не переносит), её портал удаляет
  // общий `#portal_modal_container`, и вопрос закрывается уже отключённым — фокус уходит в body,
  // а не на кнопку, открывшую форму.
  const isDiscardingRef = useRef(false);

  const stay = useCallback(() => setIsAskingDiscard(false), []);
  const discard = useCallback(() => {
    isDiscardingRef.current = true;
    setIsAskingDiscard(false);
  }, []);

  useEffect(() => {
    if (isAskingDiscard || !isDiscardingRef.current) return;
    isDiscardingRef.current = false;
    onCloseRef.current?.();
  }, [isAskingDiscard]);

  // Окно закрыли кодом (сохранили), пока висел вопрос, — вопрос уходит вместе с окном.
  useEffect(() => {
    if (!props.show) setIsAskingDiscard(false);
  }, [props.show]);

  return (
    <>
      <ModalFrame {...props} onClose={requestClose} setDirty={setDirty} holdsOpen={holdsOpen} />
      <UDConfirmModal
        show={props.show && isAskingDiscard}
        title={t`Бросить изменения?`}
        description={t`Введённое в форме не сохранится.`}
        confirmText={t`Бросить`}
        cancelText={t`Остаться`}
        onConfirm={discard}
        onCancel={stay}
      />
    </>
  );
};

export default UDModal;
